import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, fmtDay, Invoice, INVOICE_STATUS_CLASS, money, openPdf, PAYMENT_METHOD_HU, PROVIDER_HU } from '../api';
import { useSession } from '../session';
import { ErrorBox, fmtDate, useAsync } from '../components/ui';
import { InvoiceCreate } from '../components/InvoiceCreate';
import { RecurringForm } from '../components/RecurringForm';

const PENDING = new Set(['queued', 'issuing']);

export function InvoicePage() {
  const { id } = useParams();
  const s = useSession();
  const nav = useNavigate();
  const [inv, setInv] = useState<Invoice | null>(null);
  const [pay, setPay] = useState({ amount: '', paid_on: new Date().toISOString().slice(0, 10), method: 'transfer' });
  const [reminder, setReminder] = useState<string | null>(null);
  const [fromProforma, setFromProforma] = useState(false);
  const [recur, setRecur] = useState(false);
  const [recurSaved, setRecurSaved] = useState(false);
  const load = useAsync();
  const act = useAsync();
  const timer = useRef<number>();
  const canIssue = s.role === 'owner' || s.role === 'finance';
  const reload = () => load.run(async () => {
    const r = await api<Invoice>(`/invoices/${id}`);
    setInv(r);
    window.clearTimeout(timer.current);
    if (PENDING.has(r.status) || (r.number && !r.has_pdf)) timer.current = window.setTimeout(reload, 2000);
  });
  useEffect(() => { reload(); return () => window.clearTimeout(timer.current); }, [id]);
  const post = async (path: string, body?: unknown, confirm?: string) => {
    if (confirm && !window.confirm(confirm)) return;
    const r = await act.run(() => api<any>(`/invoices/${id}/${path}`, { method: 'POST', body }));
    if (r?.id && r.id !== id && r.kind) nav(`/szamlak/${r.id}`); else if (r) reload();
    return r;
  };
  if (load.error && !inv) return <ErrorBox error={load.error} />;
  if (!inv) return <div className="muted">Betöltés…</div>;
  const open = inv.kind === 'invoice' && ['issued', 'partially_paid'].includes(inv.status);
  return (
    <div className="detail">
      <div className="page-head">
        <h2>{inv.kind_hu[0].toUpperCase() + inv.kind_hu.slice(1)} {inv.number ?? ''} <span className={`badge ${INVOICE_STATUS_CLASS[inv.status] ?? ''}`}>{inv.status_hu}</span>
          {inv.overdue && <span className="badge badge-risk-external">{inv.days_late} napja lejárt</span>}</h2>
        <div className="spacer" />
        <Link to="/szamlak">← Számlák</Link>
        {inv.has_pdf && <button className="secondary" onClick={() => act.run(() => openPdf(`/invoices/${id}/pdf`))}>PDF</button>}
      </div>
      {PENDING.has(inv.status) && <div className="notice">A(z) {PROVIDER_HU[inv.provider] ?? inv.provider} éppen kiállítja… (néhány másodperc)</div>}
      {inv.error && <div className="error">{inv.error}</div>}
      <div className="actions">
        {['failed', 'unknown'].includes(inv.status) && canIssue && <button onClick={() => post('retry', undefined, inv.status === 'unknown' ? 'Biztosan nincs meg a bizonylat a számlázóban? Ha ott van, ne indítsd újra (kétszer jönne létre).' : undefined)}>Újra</button>}
        {inv.kind === 'proforma' && ['issued', 'paid', 'partially_paid'].includes(inv.status) && canIssue && !inv.related?.some((r) => r.kind === 'invoice' && r.status !== 'failed') &&
          <button onClick={() => setFromProforma(true)}>Számla a díjbekérőből</button>}
        {open && <button className="secondary" onClick={() => setReminder('')}>Fizetési emlékeztető</button>}
        {['invoice', 'proforma'].includes(inv.kind) && !['failed', 'queued', 'issuing', 'cancelled'].includes(inv.status) && canIssue && <button className="secondary" onClick={() => setRecur(true)}>Ismétlődővé tétel</button>}
        {(open || inv.status === 'paid') && inv.kind === 'invoice' && <button className="secondary" onClick={() => post('refresh')}>Fizetés ellenőrzése</button>}
        {inv.kind === 'invoice' && ['issued', 'partially_paid', 'paid'].includes(inv.status) && canIssue && !inv.related?.some((r) => r.kind === 'cancellation' && r.status !== 'failed') &&
          <button className="danger" onClick={() => { const reason = window.prompt('Sztornó oka (a bizonylaton is megjelenhet):'); if (reason !== null) post('cancel', { reason: reason || undefined }); }}>Sztornó</button>}
      </div>
      <ErrorBox error={act.error} />
      {recurSaved && <div className="notice">Ismétlődő számla felvéve – a <Link to="/szamlak">Számlák › ismétlődő</Link> fülön kezelheted.</div>}
      {recur && <RecurringForm fromInvoiceId={inv.id} defaultTitle={inv.items?.[0]?.name} onClose={() => setRecur(false)} onSaved={() => { setRecur(false); setRecurSaved(true); }} />}
      {fromProforma && <InvoiceCreate kind="invoice" fromProformaId={inv.id} onClose={() => setFromProforma(false)} />}
      {reminder !== null && (
        <div className="modal-back" role="dialog" aria-modal="true" onClick={() => setReminder(null)}>
          <div className="modal card wide" onClick={(e) => e.stopPropagation()}>
            <h3>Fizetési emlékeztető</h3>
            <p className="muted small">A vevő kapcsolattartójának megy a csatolt postafiókodból, a számla PDF-jével. Üresen hagyva a sablonszöveg megy.</p>
            <textarea rows={8} value={reminder} onChange={(e) => setReminder(e.target.value)} placeholder="Saját szöveg (opcionális)" />
            <div className="actions">
              <button onClick={async () => { if (await post('remind', { body: reminder || undefined })) setReminder(null); }} disabled={act.busy}>Küldés</button>
              <button className="secondary" onClick={() => setReminder(null)}>Mégse</button>
            </div>
            <ErrorBox error={act.error} />
          </div>
        </div>
      )}
      <div className="cols">
        <div>
          <section className="card">
            <dl className="props">
              <dt>Vevő</dt><dd><Link to={`/cegek/${inv.company_id}`}>{inv.buyer.name}</Link><div className="muted small">{inv.buyer.zip} {inv.buyer.city}, {inv.buyer.street}{inv.buyer.tax_number && ` · ${inv.buyer.tax_number}`}</div></dd>
              <dt>Kelt</dt><dd>{fmtDay(inv.issue_date)}</dd>
              <dt>Teljesítés</dt><dd>{fmtDay(inv.fulfillment_date)}</dd>
              <dt>Fizetési határidő</dt><dd className={inv.overdue ? 'overdue' : ''}>{fmtDay(inv.due_date)}</dd>
              <dt>Fizetési mód</dt><dd>{PAYMENT_METHOD_HU[inv.payment_method]}</dd>
              {inv.related_number && <><dt>Kapcsolódó</dt><dd><Link to={`/szamlak/${inv.related_invoice_id}`}>{inv.related_number}</Link></dd></>}
              {inv.related?.map((r) => <Fragment key={r.id}><dt>{r.kind === 'cancellation' ? 'Sztornó' : 'Számla'}</dt><dd><Link to={`/szamlak/${r.id}`}>{r.number ?? '(kiállítás alatt)'}</Link></dd></Fragment>)}
              {inv.quote_id && <><dt>Ajánlat</dt><dd><Link to={`/ajanlatok/${inv.quote_id}`}>megnyitás</Link></dd></>}
              {inv.deal_id && <><dt>Deal</dt><dd><Link to={`/ertekesites/${inv.deal_id}`}>megnyitás</Link></dd></>}
              <dt>Számlázó</dt><dd>{PROVIDER_HU[inv.provider] ?? inv.provider} · kiállította: {inv.created_by_name}</dd>
              {inv.comment && <><dt>Megjegyzés</dt><dd>{inv.comment}</dd></>}
            </dl>
          </section>
          <section className="card">
            <h3>Tételek</h3>
            <table className="list items">
              <thead><tr><th>Megnevezés</th><th className="num">Menny.</th><th className="num">Egységár</th><th>ÁFA</th><th className="num">Nettó</th><th className="num">Bruttó</th></tr></thead>
              <tbody>{inv.items.map((it, k) => (
                <tr key={k}><td>{it.name}</td><td className="num">{it.quantity} {it.unit}</td><td className="num">{money(it.unit_price, inv.currency)}</td><td>{it.vat_code}</td>
                  <td className="num">{money(it.net, inv.currency)}</td><td className="num">{money(it.gross, inv.currency)}</td></tr>))}</tbody>
            </table>
            <div className="totals">
              <div><span>Nettó</span><b>{money(inv.net_total, inv.currency)}</b></div>
              <div><span>ÁFA</span><b>{money(inv.vat_total, inv.currency)}</b></div>
              <div className="grand"><span>Bruttó</span><b>{money(inv.gross_total, inv.currency)}</b></div>
            </div>
          </section>
        </div>
        <div>
          {inv.kind === 'invoice' && (
            <section className="card">
              <h3>Kifizetések</h3>
              <p>Fizetve: <b>{money(inv.paid_total, inv.currency)}</b>{inv.open_amount > 0 && <> · nyitott: <b className={inv.overdue ? 'overdue' : ''}>{money(inv.open_amount, inv.currency)}</b></>}</p>
              <ul className="plain">
                {inv.payments?.map((p) => <li key={p.id}>{fmtDay(p.paid_on)} · {money(p.amount, inv.currency)} · {PAYMENT_METHOD_HU[p.method] ?? p.method}
                  <span className="muted small"> · {p.source === 'provider' ? 'a számlázóból' : p.synced ? 'rögzítve, felküldve' : 'rögzítve, felküldés alatt'}</span></li>)}
                {!inv.payments?.length && <li className="muted">Még nincs kifizetés. A banki jóváírást a számlázó párosítja; a CRM pár óránként visszaolvassa.</li>}
              </ul>
              {open && canIssue && (
                <div className="actions">
                  <input inputMode="decimal" placeholder={String(inv.open_amount)} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} style={{ width: 120 }} aria-label="Összeg" />
                  <input type="date" value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} style={{ width: 150 }} aria-label="Dátum" />
                  <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} style={{ width: 130 }} aria-label="Mód"><option value="transfer">átutalás</option><option value="cash">készpénz</option><option value="card">bankkártya</option></select>
                  <button className="secondary" disabled={act.busy} onClick={async () => {
                    const amount = Number((pay.amount || String(inv.open_amount)).replace(/\s/g, '').replace(',', '.'));
                    if (await post('payments', { amount, paid_on: pay.paid_on, method: pay.method })) setPay({ ...pay, amount: '' });
                  }}>Kifizetés rögzítése</button>
                </div>
              )}
              {inv.reminders_sent > 0 && <p className="muted small">Emlékeztető: {inv.reminders_sent} db, utoljára {inv.last_reminder_at ? fmtDate(inv.last_reminder_at) : ''}</p>}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
